import { v2 } from "./short3";
import { pairwise } from "oc-commons/Commons";
import { Vector2, Box2, Matrix3 } from "three";

/**
 * Statische Utility-Klasse zur Mathematik im euklidischen Raum
 *
 * STATUS: ungetestet da Wissenexport aus pina!
 */

/**
 * Standardvektoren
 */
export const vector = {
  zero: v2(0, 0),
  x: v2(1, 0),
  y: v2(0, 1),
};

/**
 * Gibt den Schnittpunkt zweier Kreise zurück, die jeweils durch ihren Mittelpunkt und ihren Radius gegeben sind
 */
export function intersectCircles(
  center1: Vector2,
  radius1: number,
  center2: Vector2,
  radius2: number,
): [Vector2, Vector2] | [Vector2] | [] {
  const dV = center2.clone().sub(center1);
  const d = dV.length();
  if (d > radius1 + radius2 || d + radius1 < radius2 || d + radius2 < radius1 || d == 0) {
    //kein Schnittpunkt/beliebig viele
    return [];
  } else if (d == radius1 + radius2 || radius1 == radius2 + d || radius2 == radius1 + d) {
    //ein schnittpunkt
    return [center1.clone().add(dV.normalize().multiplyScalar(radius1))];
  } else {
    //zwei Schnittpunkte
    const A = triangleAreaSSS(d, radius1, radius2);
    if (A === null) return [];
    //Höhe auf d aus Fläche=grundseite*Höhe/2
    const h = (A / d) * 2;
    //Abschnitte auf Basis des Höhenfußpunktes (d1+d2==d)
    const d1 = Math.sqrt(radius1 * radius1 - h * h);
    // const d2 = Math.sqrt(radius2 * radius2 - h * h);

    const ortD = getOrthogonalVector2(dV);
    const fußPunkt = center1.clone().add(dV.normalize().multiplyScalar(d1));

    return [
      fußPunkt.clone().add(ortD.multiplyScalar(h)),
      fußPunkt.clone().add(ortD.multiplyScalar(-1)),
    ];
  }
}

/**
 * einfacher und vermutlich besser als getOrthogonalVector2
 */
export function orthogonalLeft(vector: Vector2): Vector2 {
  return v2(vector.y, -vector.x);
}

/**
 * einfacher und vermutlich besser als getOrthogonalVector2
 */
export function orthogonalRight(vector: Vector2): Vector2 {
  return v2(-vector.y, vector.x);
}

/**
 * Gibt einen normalisierten Vector zurück, der senkrecht auf dem Eingebenen steht
 */
export function getOrthogonalVector2(vector: Vector2): Vector2 {
  let ortD;
  if (vector.x != 0) {
    ortD = new Vector2(-vector.y / vector.x, 1).normalize();
  } else if (vector.y != 0) {
    ortD = new Vector2(1, -vector.x / vector.y).normalize();
  } else {
    //null Vektor, alle stehen senkrecht drauf, auch die Eingabe
    ortD = vector;
  }
  return ortD;
}

/**
 * Gibt die Fläche des Dreiecks zurück das durch die Kantenlängen a,b,c gegebn wird
 * nutzt Satz of Horn
 */
export function triangleAreaSSS(a: number, b: number, c: number): number | null {
  if (checkCanBeTriangle(a, b, c)) {
    const s = 0.5 * (a + b + c);
    const A = Math.sqrt(s * (s - a) * (s - b) * (s - c));
    return A;
  } else {
    console.warn("no triangle given");
    return null;
  }
}

/**
 * Prüft ob die Seiten a,b und c den Dreiecksungleichungen genügen
 */
export function checkCanBeTriangle(a: number, b: number, c: number): boolean {
  return a + b > c && a + c > b && b + c > a;
}

/**
 * Berechnet den Mittelpunkt aus n vektoren
 */
export function middle(vectors: Vector2[]): Vector2 {
  if (vectors.length < 3) {
    return centerOfGravity(vectors);
  }
  return new Box2().setFromPoints(vectors).getCenter(v2());
}

/**
 * Berechnet den Mittelpunkt aus n vektoren
 */
export function distance([a, b]: [Vector2, Vector2]): number {
  return a.distanceTo(b);
}

/**
 * Berechnet den Schwerpunkt aus n vektoren
 */
export function centerOfGravity(vectors: Vector2[]): Vector2 {
  const m = v2();
  vectors.forEach((v) => m.add(v));
  m.multiplyScalar(1 / vectors.length);
  return m;
}

/**
 * Prüft anhand des Ray-Casting-Algorithmus, ob ein Punkt innerhalb eines Polygons liegt,
 * indem die Anzahl der Schnitte in eine Richtung mit den Polygonkanten gezählt wird.
 * Funktioniert auch für konkave Polygone.
 */
export function isPointInPolygon(point: Vector2, polygon: Vector2[]): boolean {
  let inside = false;
  let j = polygon.length - 1;

  for (let i = 0; i < polygon.length; j = i++) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;

    const intersect =
      yi > point.y !== yj > point.y && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
}

export type IntersectionType = "X" | "T" | "L" | "I";

export interface IntersectionResult {
  type: IntersectionType;
  point: Vector2 | null;
  segmentIndex?: number;
}

/**
 * Findet alle Schnittpunkte zwischen einer Linie und einem Polygon.
 *
 * @param {Vector2[]} line - Die Linie, die auf Schnittpunkte geprüft werden soll.
 * @param {Vector2[]} polygon - Das Polygon, das keine Löcher hat und eine einheitliche Umlaufrichtung besitzt.
 * @param {number} epsilon - Sollte auf einen sinnvollen Wert gesetzt werden, z.B. 0.1 mm - die Polygon-Intersection-Prüfing klappt sonst eventuell nicht
 * @returns {Array<{type: "X"|"T"|"L"|"I", point: Vector2|null, segmentIndex: number}>} - Eine Liste von Schnittpunkten und deren Typen.
 */
export function findLinePolygonIntersections(
  line: Vector2[],
  polygon: Vector2[],
  epsilon = Number.EPSILON,
): IntersectionResult[] {
  return pairwise(polygon)
    .map((polygonSegment: Vector2[], segmentIndex: number) => {
      const intersection = segmentIntersection(line, polygonSegment, epsilon);
      if (intersection == null) {
        return null;
      }
      return { ...intersection, segmentIndex };
    })
    .filter((i: IntersectionResult | null): i is IntersectionResult => i != null);
}

/**
 * Unterteilt eine Strecke, so dass sie mehr Punkte enthält, gleichmässig verteilt
 */
function subdivideLineSegment([a, b]: [Vector2, Vector2], resolution: number): Vector2[] {
  const distance = a.distanceTo(b);
  if (distance < resolution * 2) {
    return [a, b];
  }
  const step = distance / Math.floor(distance / resolution);
  const direction = getDirection(a, b);
  const list = [a];
  for (let i = step; i < distance - 0.01; i += step) {
    const v = a.clone().addScaledVector(direction, i);
    list.push(v);
  }
  list.push(b);
  return list;
}

/**
 * Unterteilt den Umriss eines Polygons, so dass er mehr Punkte enthält, gleichmässig verteilt
 * @param {Vector2[]} polygon - Polygon, das keine Löcher hat und eine einheitliche Umlaufrichtung besitzt.
 * @param {number} resolution
 * @returns {*}
 */
export function subdividePolygon(polygon: Vector2[], resolution: number): Vector2[] {
  const result = polygon.slice(0, 1);
  for (let i = 1; i < polygon.length; i++) {
    const a = polygon[i - 1];
    const b = polygon[i];
    const additionalPoints = subdivideLineSegment([a, b], resolution).slice(1);
    result.push(...additionalPoints);
  }
  return result;
}

/**
 * gibt die 4 Eckpunkte einer box2 zurück
 */
export function box2ToVertices(box2: Box2): Vector2[] {
  return [
    v2(box2.min.x, box2.min.y),
    v2(box2.min.x, box2.max.y),
    v2(box2.max.x, box2.max.y),
    v2(box2.max.x, box2.min.y),
  ];
}

/**
 * prüft, ob a und b in entgegengesetzte Richtung zeigen
 */
export function areAnticollinear(a: Vector2, b: Vector2, epsilon = 1e-6): boolean {
  const crossProduct = a.x * b.y - a.y * b.x;
  const dotProduct = a.dot(b);
  return Math.abs(crossProduct) < epsilon && dotProduct < 0;
}

/**
 * prüft, ob die Eckpunkte eines Polygons in Uhrzeigersinn oder im Gegenuhrzeigersinn orientiert sind.
 */
export function isPolygonClockwise(polygon: Vector2[]): boolean {
  let sum = 0;
  for (let i = 0; i < polygon.length; i++) {
    const p1 = polygon[i];
    const p2 = polygon[(i + 1) % polygon.length];
    sum += (p2.x - p1.x) * (p2.y + p1.y);
  }
  return sum > 0;
}

/**
 * Berechnet zu einem Punkt den nahegelegensten Punkt auf einem Liniensegment.
 * @returns {THREE.Vector2} - Der kürzeste Abstand vom Punkt zum Liniensegment.
 */
export function closestPointToLineSegment(point: Vector2, segment: Vector2[]): Vector2 {
  const [a, b] = segment;
  if (a.manhattanDistanceTo(b) < Number.EPSILON) {
    return a.clone();
  }
  const ap = v2().subVectors(point, a);
  const ab = v2().subVectors(b, a);
  const projection = Math.max(0, Math.min(1, ap.dot(ab) / ab.lengthSq()));
  const closestPoint = ab.multiplyScalar(projection).add(a);
  return closestPoint;
}

/**
 * Berechnet den kürzesten Abstand eines Punktes zu einem Liniensegment.
 * @returns {number} - Der kürzeste Abstand vom Punkt zum Liniensegment.
 */
export function distanceToLineSegment(point: Vector2, segment: Vector2[]): number {
  const closestPoint = closestPointToLineSegment(point, segment);
  return closestPoint.distanceTo(point);
}

/**
 * Berechnet, ob sich zwei unendliche Linien schneiden, oder null falls parallel
 * @returns {Vector2|null} intersection or null if parallel
 */
export function lineInfiniteIntersection(
  lineA: Vector2[],
  lineB: Vector2[],
  epsilon = Number.EPSILON,
): Vector2 | null {
  const x1 = lineA[0].x;
  const y1 = lineA[0].y;
  const x2 = lineA[1].x;
  const y2 = lineA[1].y;
  const x3 = lineB[0].x;
  const y3 = lineB[0].y;
  const x4 = lineB[1].x;
  const y4 = lineB[1].y;

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  if (Math.abs(denominator) <= epsilon) {
    return null;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const x = x1 + ua * (x2 - x1);
  const y = y1 + ua * (y2 - y1);

  return v2(x, y);
}

/**
 * Berechnet, ob sich zwei Liniensegmente schneiden, und gibt die Art des Schnittpunktes zurück.
 *
 * Die Art des Schnittpunkte:
 *
 * - "X": X-Kreuzung mit einem Schnittpunkt
 * - "T": T-Kreuzung mit einem Schnittpunkt, der Eckpunkt einer Linie ist
 * - "L": L-Kreuzung mit einem Schnittpunkt, der gemeinsamer Eckpunkt beider Linien ist
 * - "I": intersection, Linien sind kollinear und überlappen sich (Schnittpunkt sollte ignoriert werden, ist meist null)
 *
 * andernfalls wird null zurückgegeben.
 *
 * @param {number} epsilon - Sollte auf einen sinnvollen Wert gesetzt werden, z.B. 0.1 mm - die Intersection-Prüfing klappt sonst eventuell nicht
 * @returns {{type: "X"|"T"|"L"|"I", point: Vector2|null}|null}
 */
export function segmentIntersection(
  segmentA: Vector2[],
  segmentB: Vector2[],
  epsilon = Number.EPSILON,
): IntersectionResult | null {
  const [p0, p1] = segmentA;
  const [p2, p3] = segmentB;

  const onSegment = (p: Vector2, q: Vector2, r: Vector2) =>
    Math.min(p.x, q.x) <= r.x + epsilon &&
    r.x <= Math.max(p.x, q.x) + epsilon &&
    Math.min(p.y, q.y) <= r.y + epsilon &&
    r.y <= Math.max(p.y, q.y) + epsilon;

  const intersection = lineInfiniteIntersection(segmentA, segmentB, epsilon);
  if (intersection) {
    if (onSegment(p0, p1, intersection) && onSegment(p2, p3, intersection)) {
      const points = [p0, p1, p2, p3].filter((p) => intersection.distanceTo(p) < epsilon);
      const type = (["X", "T", "L", "I"] as const)[points.length];
      return { type, point: intersection };
    }
    // windschief
    return null;
  }
  if (
    onSegment(p0, p1, p2) ||
    onSegment(p0, p1, p3) ||
    onSegment(p2, p3, p0) ||
    onSegment(p2, p3, p1)
  ) {
    return { type: "I", point: null };
  }
  // parallel
  return null;
}

/**
 * Entfernt Duplikate aus einer Menge von Punkten in O(n*log(n))
 */
export function uniquePoints(points: Vector2[]): Vector2[] {
  points.sort((a, b) => {
    if (a.x === b.x) {
      return a.y - b.y;
    }
    return a.x - b.x;
  });

  return points.filter(
    (point, index, self) =>
      index === 0 || point.x !== self[index - 1].x || point.y !== self[index - 1].y,
  );
}

/**
 * gibt die Normale zu einer Strecke ab bevorzugt in Richtung Punkt c zurück
 */
export function calculateNormalTowardsC(a: Vector2, b: Vector2, c: Vector2): Vector2 {
  const ab = v2().subVectors(b, a);
  const normal = v2(-ab.y, ab.x);
  const ac = v2().subVectors(c, a);
  if (ac.dot(normal) < 0) {
    normal.multiplyScalar(-1);
  }
  normal.normalize();
  return normal;
}

export function getDirection(a: Vector2, b: Vector2): Vector2 {
  return b.clone().sub(a).normalize();
}

export function isParallel(a: Vector2, b: Vector2, epsilon = Number.EPSILON): boolean {
  return Math.abs(a.angleTo(b)) <= epsilon;
}

export function isOrthogonal(a: Vector2, b: Vector2, epsilon = Number.EPSILON): boolean {
  return Math.abs(a.angleTo(b) - Math.PI / 2) <= epsilon;
}

/**
 * vereinigt zwei kollineare Strecken
 */
export function mergeCollinear(a: Vector2[], b: Vector2[]): [Vector2, Vector2] {
  const direction = getDirection(a[0], a[1]);
  const points = [...a, ...b];
  sortCollinear(direction, points);
  return [points[0], points[points.length - 1]];
}

export function sortCollinear(direction: Vector2, points: Vector2[]): Vector2[] {
  points.sort((a, b) => direction.dot(a) - direction.dot(b));
  return points;
}

/**
 * Gibt die Schnittpunkte einer Strecke mit einem Kreis zurück
 * @param {boolean} alsoWithInner gibt die Linien-Punkte als Schnittpunkte zurück, wenn sie innerhalb des Circle liegen
 */
export function lineCircleIntersections(
  line: Vector2[],
  circle: { center: Vector2; radius: number },
  alsoWithInner = false,
): Vector2[] {
  const [lineStart, lineEnd] = line;
  // Vektor von lineStart zu lineEnd
  const d = lineEnd.clone().sub(lineStart);
  // Vektor vom lineStart zum Kreismittelpunkt
  const f = lineStart.clone().sub(circle.center);
  const r = circle.radius;
  // Koeffizienten der quadratischen Gleichung a*t^2 + b*t + c = 0
  const a = d.dot(d); // dx^2 + dy^2
  const b = 2 * f.dot(d);
  const c = f.dot(f) - r * r;
  // Diskriminante
  const D = b * b - 4 * a * c;
  const intersections: Vector2[] = [];
  if (D >= 0) {
    const t1 = (-b - Math.sqrt(D)) / (2 * a);
    const t2 = (-b + Math.sqrt(D)) / (2 * a);
    if (t1 >= 0 && t1 <= 1) {
      intersections.push(v2(lineStart.x + t1 * d.x, lineStart.y + t1 * d.y));
    }
    if (t2 >= 0 && t2 <= 1 && D > 0) {
      intersections.push(v2(lineStart.x + t2 * d.x, lineStart.y + t2 * d.y));
    }
  }
  if (alsoWithInner) {
    // Überprüfe, ob lineStart oder lineEnd innerhalb des Kreises liegen
    if (lineStart.distanceTo(circle.center) < r && intersections.length < 2) {
      intersections.push(lineStart.clone());
    }
    if (lineEnd.distanceTo(circle.center) < r && intersections.length < 2) {
      intersections.push(lineEnd.clone());
    }
  }
  return intersections;
}

/**
 * Gibt die Schnittpunkte einer Strecke mit einer Box2 zurück
 * @param {boolean} alsoWithInner gibt die Linien-Punkte als Schnittpunkte zurück, wenn sie innerhalb der Box liegen
 */
export function lineBox2Intersections(
  line: Vector2[],
  box: Box2,
  alsoWithInner = false,
): Vector2[] {
  // Liang-Barsky-Algorithmus
  let t0 = 0.0; // Start des Linienabschnitts
  let t1 = 1.0; // Ende des Linienabschnitts
  const [A, B] = line;
  const dx = B.x - A.x;
  const dy = B.y - A.y;

  const p = [-dx, dx, -dy, dy];
  const q = [A.x - box.min.x, box.max.x - A.x, A.y - box.min.y, box.max.y - A.y];

  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) {
        return []; // Linie parallel zur Box-Kante und außerhalb der Box
      }
    } else {
      const t = q[i] / p[i];
      if (p[i] < 0 && t > t0) {
        t0 = t; // Update t0 für Eintritt
      } else if (p[i] > 0 && t < t1) {
        t1 = t; // Update t1 für Austritt
      }
    }
  }

  if (t0 > t1) {
    return []; // Kein Schnittpunkt
  }

  const intersects: Vector2[] = [];

  if (t0 > 0) intersects.push(new Vector2(A.x + t0 * dx, A.y + t0 * dy)); // Schnittpunkt bei Eintritt
  if (t1 < 1) intersects.push(new Vector2(A.x + t1 * dx, A.y + t1 * dy)); // Schnittpunkt bei Austritt

  if (alsoWithInner) {
    if (box.containsPoint(A) && intersects.length < 2) {
      intersects.push(A);
    }
    if (box.containsPoint(B) && intersects.length < 2) {
      intersects.push(B);
    }
  }

  return intersects;
}

/**
 * Prüft ob eine Box2 einen Kreis schneidet
 */
export function box2IntersectsCircle(
  box: Box2,
  circle: { center: Vector2; radius: number },
): boolean {
  // Find the closest point on the box to the circle center
  const x = Math.max(box.min.x, Math.min(circle.center.x, box.max.x));
  const y = Math.max(box.min.y, Math.min(circle.center.y, box.max.y));

  // Calculate the distance between this closest point and the circle's center
  const distanceX = x - circle.center.x;
  const distanceY = y - circle.center.y;
  const distanceSquared = distanceX * distanceX + distanceY * distanceY;

  // If the distance is less than the circle's radius squared, they intersect
  return distanceSquared < circle.radius * circle.radius;
}

/**
 * erstellt eine TranformationsMatrix, die eine Linie so transformiert, dass der Startpunkt
 * im Ursprung und der Endpunkt auf der X-Achse liegt, und die Länge beibehalten wird.
 * @param {Vector2[]} line
 * @returns {Matrix3}
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function transformLineToAxis(line: Vector2[]): Matrix3 {
  const [start, end] = line;
  const direction = end.clone().sub(start);
  const angle = direction.angle();
  const translationMatrix = new Matrix3().makeTranslation(-start.x, -start.y);
  const rotationMatrix = new Matrix3().makeRotation(-angle);
  const transformationMatrix = new Matrix3().multiplyMatrices(rotationMatrix, translationMatrix);
  return transformationMatrix;
}

/**
 * Unterteilt einen Path an der Stelle atDistance als neuen Punkt in zwei Teile
 * @param {number} epsilon für den Grenzfall, dass atDistance genau auf vorhandene Punkte fällt
 * @returns [Vector2[],Vector2[]] den ersten Pfad bis atDistance und den zweiten Teil von atDistance
 */
export function splitPath(
  path: Vector2[],
  atDistance: number,
  epsilon = Number.EPSILON,
): [Vector2[], Vector2[]] {
  let s = 0;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1];
    const b = path[i];
    const d = a.distanceTo(b);
    const diff = s + d - atDistance;
    if (Math.abs(diff) < epsilon) {
      const path0 = path.slice(0, i);
      const path1 = path.slice(i);
      return [path0, path1];
    } else if (diff > 0) {
      const rest = atDistance - s;
      const alpha = rest / d;
      const middle = a.clone().lerp(b, alpha);
      const path0 = [...path.slice(0, i), middle];
      const path1 = [middle, ...path.slice(i)];
      return [path0, path1];
    }
    s += d;
  }
  return [path, []];
}

/**
 * Gesamtdistance eines Pfades
 */
export function sumPathDistance(path: Vector2[]): number {
  let sum = 0;
  for (let i = 1; i < path.length; i++) {
    sum += path[i - 1].distanceTo(path[i]);
  }
  return sum;
}

export interface DistancedLine {
  line: Vector2[];
  radius: number;
}

/**
 * Gibt die Schnittpunkte einer Strecke mit einer "umrandeten" Strecke zurück
 * @param {boolean} alsoWithInner gibt die Linien-Punkte als Schnittpunkte zurück, wenn sie innerhalb der distancedLine liegen
 */
export function lineLineDistancedIntersections(
  line: Vector2[],
  distancedLine: DistancedLine,
  alsoWithInner = false,
): Vector2[] {
  const intersections = distancedLine.line.flatMap((center) =>
    lineCircleIntersections(line, { center, radius: distancedLine.radius }, alsoWithInner),
  );

  const ray = new Ray2().setFromPoints(line);
  const intervalle = intersections.map((p) => ray.distanceOriginToPointProjection(p));
  if (intervalle.length < 2) {
    return [];
  }
  const interval = [Math.min(...intervalle), Math.max(...intervalle)];
  if (interval[1] - interval[0] < 0.0001) {
    return [];
  }
  const points = interval.map((i) => ray.at(i));
  return points;
}

export function isMultipleOfAngle(
  angle: number,
  multipleOf: number,
  epsilon = Number.EPSILON,
): boolean {
  const c = (angle + 2 * Math.PI) % multipleOf;
  return c < epsilon || c > multipleOf - epsilon;
}

export class Ray2 {
  origin: Vector2;
  direction: Vector2;

  /**
   * @param {Vector2} origin
   * @param {Vector2} direction
   */
  constructor(origin = v2(), direction = v2()) {
    this.origin = origin;
    this.direction = direction;
  }

  /**
   * @param {Vector2[]} points
   * @param {this}
   */
  setFromPoints([origin, target]: Vector2[]): this {
    this.origin = origin.clone();
    this.direction = target.clone().sub(origin).normalize();
    return this;
  }

  /**
   * Distanz vom origin zur Projektion des Punktes auf diese Ray
   * @param {Vector2} point
   * @returns {number}
   */
  distanceOriginToPointProjection(point: Vector2): number {
    return this.direction.dot(point.clone().sub(this.origin));
  }

  /**
   * @param {number} distance vom origin
   * @returns {Vector2}
   */
  at(distance: number): Vector2 {
    return this.origin.clone().addScaledVector(this.direction, distance);
  }

  /**
   * Get the distance of the closest approach between the Ray and the point.
   * @param {Vector2} point
   * @returns {Vector2}
   */
  closestPointToPoint(point: Vector2): Vector2 {
    return this.at(this.distanceOriginToPointProjection(point));
  }

  /**
   * Get the distance of the closest approach between the Ray and the point.
   * @param {Vector2} point
   * @returns {number}
   */
  distanceToPoint(point: Vector2): number {
    return this.closestPointToPoint(point).distanceTo(point);
  }

  /**
   * Get the intersection Point of this ray with the line segment or null
   * @param {Vector2[]} segment
   * @returns {Vector2|null}
   */
  intersectsSegment(segment: Vector2[]): Vector2 | null {
    const a = this.origin;
    const b = this.origin.clone().add(this.direction);
    const p = lineInfiniteIntersection([a, b], segment);
    const onSegment = ([p, q]: Vector2[], r: Vector2) =>
      Math.min(p.x, q.x) <= r.x &&
      r.x <= Math.max(p.x, q.x) &&
      Math.min(p.y, q.y) <= r.y &&
      r.y <= Math.max(p.y, q.y);
    return p && onSegment(segment, p) && getDirection(a, p).dot(this.direction) >= 0 ? p : null;
  }
}

export const cross = (ax: number, ay: number, bx: number, by: number): number => ax * by - ay * bx;

/**
 * Gibt die konvexe Hülle einer Punktmenge zurück
 */
export function convexHull(points: Vector2[]): Vector2[] {
  const pts = points.map((p) => v2(p.x, p.y)).sort((p, q) => (p.x === q.x ? p.y - q.y : p.x - q.x));
  const lower: Vector2[] = [];
  for (const p of pts) {
    while (lower.length >= 2) {
      const a = lower[lower.length - 2];
      const b = lower[lower.length - 1];
      if (cross(b.x - a.x, b.y - a.y, p.x - b.x, p.y - b.y) <= 0) lower.pop();
      else break;
    }
    lower.push(p);
  }
  const upper: Vector2[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (upper.length >= 2) {
      const a = upper[upper.length - 2];
      const b = upper[upper.length - 1];
      if (cross(b.x - a.x, b.y - a.y, p.x - b.x, p.y - b.y) <= 0) upper.pop();
      else break;
    }
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}
